import { Box, Flex, Skeleton, Text } from '@chakra-ui/react'
import React from 'react'
import { useUserState } from '~/atoms/UserState'
import { useGetCallsReport } from '~/queries/reportingQueries'
import { DateRange } from '~/types/Shared'
import { getGroupBy } from '~/utils/dashboardUtils'

import { CallsReportData, CallsReportRequest } from '@mangovoice/types'

import { toLocalDateTimeString } from '../Shared/LocalTimeHelper'
import RechartsBarChart from '../Shared/RechartsBarChart'
import StatsGrid, { StatsMetric } from '../Shared/StatsGrid'

const metrics: StatsMetric[] = [
  {
    label: 'Inbound Calls',
    compute: (data) =>
      data.reduce((sum, row) => sum + Number(row['calls/inbound'] || 0), 0),
  },
  {
    label: 'Outbound Calls',
    compute: (data) =>
      data.reduce((sum, row) => sum + Number(row['calls/outbound'] || 0), 0),
  },
  {
    label: 'Total Calls',
    compute: (data) =>
      data.reduce(
        (sum, row) =>
          sum +
          Number(row['calls/inbound'] || 0) +
          Number(row['calls/outbound'] || 0),
        0,
      ),
  },
  {
    label: 'Average Calls',
    compute: (data) => {
      const total = data.reduce(
        (sum, row) =>
          sum +
          Number(row['calls/inbound'] || 0) +
          Number(row['calls/outbound'] || 0),
        0,
      )
      return data.length ? total / data.length : 0
    },
  },
]

interface CallVolumeDashboardProps {
  sharedDateRange?: DateRange
}

const CallVolumeDashboard = ({ sharedDateRange }: CallVolumeDashboardProps) => {
  // Determine the groupBy value based on the number of days.
  const { groupBy, groupByTitle } = getGroupBy(sharedDateRange)
  const { pbxId } = useUserState()

  const beforeDate = sharedDateRange?.before ?? new Date()
  const afterDate = sharedDateRange?.after ?? new Date()
  const adjustedBefore = new Date(
    beforeDate.getFullYear(),
    beforeDate.getMonth(),
    beforeDate.getDate(),
    23,
    59,
    59,
  )
  const startDateStr = toLocalDateTimeString(afterDate)
  const endDateStr = toLocalDateTimeString(adjustedBefore)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const reportRequest: CallsReportRequest = {
    filters: {
      startDate: startDateStr,
      endDate: endDateStr,
      pbxIds: [pbxId ?? 0],
      timezone: timeZone,
    },
    metrics: [{ field: 'calls', type: 'count' }],
    groupBy: [groupBy],
    splitBy: ['direction'],
  }

  const {
    data: callsReport,
    isLoading,
    error,
  } = useGetCallsReport({
    reportRequest: reportRequest,
  })
  if (isLoading || !sharedDateRange) {
    return (
      <Flex direction="column" w="100%" h="100%" gap="1.25rem">
        <Flex
          flexDirection={{
            base: 'column',
            xs: 'row',
            sm: 'row',
            md: 'row',
            lg: 'row',
          }}
          gap="1.25rem"
          w="100%"
        >
          <Flex flex="0 0 200px">
            <Skeleton h="400px" w="100%" />
          </Flex>
          <Flex flex="1">
            <Skeleton h="430px" w="100%" />
          </Flex>
        </Flex>

        <Box height="1px" flexShrink={0} />
      </Flex>
    )
  }

  if (error) {
    return <Text>Error loading call data</Text>
  }

  if (!callsReport || !callsReport.data || callsReport.data.length === 0) {
    return <Text>No call data available</Text>
  }

  // Fix the name of the fields to be more readable.
  const transformedCallsReport = {
    ...callsReport,
    data: callsReport.data.map((entry) => {
      const newEntry: CallsReportData = { ...entry }
      newEntry['Inbound'] = newEntry['calls/inbound'] ?? 0
      newEntry['Outbound'] = newEntry['calls/outbound'] ?? 0
      delete newEntry['calls/inbound']
      delete newEntry['calls/outbound']
      delete newEntry['calls/internal']
      return newEntry
    }),
  }

  return (
    <Flex w="100%" h="100%" flexDirection="column" gap="1.25rem">
      <Flex
        flexDirection={{
          base: 'column',
          xs: 'row',
          sm: 'row',
          md: 'row',
          lg: 'row',
        }}
        gap="1.25rem"
        w="100%"
      >
        <Flex flex="0 0 200px">
          <StatsGrid
            data={callsReport}
            metrics={metrics}
            title="Call Volume Statistics"
          />
        </Flex>
        <Flex flex="1">
          <RechartsBarChart
            chartData={transformedCallsReport}
            color={['mask.mango.teal.90', 'mask.mango.purple.80']}
            title={`Calls Per ${groupByTitle}`}
            height={430}
            showAsPercent={false}
            hasModalButton={true}
          />
        </Flex>
      </Flex>

      {/* 
        This forces spacing at the bottom of the scrollbar so we don't 
        have it stop right at the bottom of the last chart.
      */}
      <Box height="1px" flexShrink={0} />
    </Flex>
  )
}

export default CallVolumeDashboard
